import { useState, FormEvent } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../../home/components/Navbar";
import Footer from "../../home/components/Footer";
import { getUmrahBySlug } from "../../../mocks/umrah";

const FORM_URL = "https://readdy.ai/api/form/d725hvuuimmp87kg1udg";

const TAG_COLORS: Record<string, string> = {
  ECONOMY: "bg-amber-500",
  "3 STAR": "bg-cyan-600",
  "4 STAR": "bg-green-600",
  "5 STAR": "bg-amber-600",
};

export default function UmrahDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const pkg = slug ? getUmrahBySlug(slug) : undefined;

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [city, setCity] = useState("");
  const [departureDate, setDepartureDate] = useState("");
  const [pilgrims, setPilgrims] = useState("2");
  const [message, setMessage] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [openDay, setOpenDay] = useState<number | null>(1);

  if (!pkg) {
    return (
      <div className="min-h-screen bg-white">
        <Navbar />
        <div className="pt-32 text-center px-6">
          <i className="ri-moon-line text-5xl text-gray-300 mb-4 block"></i>
          <h1 className="text-2xl font-bold text-gray-800 mb-2">Package Not Found</h1>
          <p className="text-gray-500 text-sm mb-6">This Umrah package page doesn&apos;t exist or has been removed.</p>
          <button type="button" onClick={() => navigate("/umrah")}
            className="bg-cyan-600 text-white px-6 py-2.5 rounded-md text-sm font-semibold cursor-pointer hover:bg-cyan-700 transition-colors whitespace-nowrap">
            View All Packages
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  const tagColor = TAG_COLORS[pkg.tag] || "bg-gray-600";

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setError("Please enter your name."); return; }
    if (!phone.trim()) { setError("Please enter your phone number."); return; }
    if (!city.trim()) { setError("Please enter your city."); return; }
    if (!departureDate) { setError("Please select your preferred departure date."); return; }
    if (message.length > 500) { setError("Message must be 500 characters or fewer."); return; }
    setError("");
    setLoading(true);
    try {
      const body = new URLSearchParams({
        "Package": pkg.title,
        "Hotel Rating": pkg.label,
        "Duration": `${pkg.nights} Nights / ${pkg.days} Days`,
        "Hotel Makkah": pkg.hotelMakkah,
        "Hotel Madinah": pkg.hotelMadinah,
        "Your Name": name,
        "Phone": `+92${phone}`,
        "Email": email || "Not provided",
        "City": city,
        "Preferred Departure": departureDate,
        "No. of Pilgrims": pilgrims,
        "Message": message || "—",
      });
      await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });
      setSubmitted(true);
    } catch {
      setError("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Hero */}
      <div className="pt-16">
        <div className="relative w-full h-72 md:h-96 overflow-hidden">
          <img src={pkg.heroImage} alt={pkg.title} className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/30 to-transparent" />
          <div className="absolute top-0 left-0 right-0 h-1.5 bg-amber-400" />
          <div className="absolute bottom-0 left-0 right-0 px-4 md:px-12 pb-8">
            <div className="max-w-7xl mx-auto">
              <p className="text-amber-300 text-xs font-bold tracking-widest uppercase mb-2">
                <a href="/umrah" className="hover:underline cursor-pointer">Umrah Packages</a>
                <span className="mx-2 text-white/40">/</span>
                {pkg.label}
              </p>
              <div className="flex items-center gap-3 flex-wrap mb-2">
                <span className={`${tagColor} text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide whitespace-nowrap`}>
                  {pkg.label}
                </span>
                <span className="bg-white/20 text-white text-xs font-semibold px-3 py-1 rounded-full whitespace-nowrap">
                  {pkg.nights} Nights / {pkg.days} Days
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight">{pkg.title}</h1>
              <p className="text-white/80 text-sm mt-1">{pkg.subtitle}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Bar */}
      <div className="bg-cyan-600 text-white">
        <div className="max-w-7xl mx-auto px-4 md:px-12 py-3 grid grid-cols-2 md:grid-cols-4 gap-2 md:divide-x divide-white/20">
          {[
            { label: "Duration", value: `${pkg.nights}N / ${pkg.days}D` },
            { label: "Makkah Hotel", value: pkg.distanceMakkah },
            { label: "Madinah Hotel", value: pkg.distanceMadinah },
            { label: "Price", value: pkg.priceFrom },
          ].map(item => (
            <div key={item.label} className="text-center px-3 py-1">
              <p className="text-white/70 text-xs font-medium">{item.label}</p>
              <p className="text-white font-bold text-sm mt-0.5 leading-snug">{item.value}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 md:px-12 py-10">
        <div className="flex flex-col lg:flex-row gap-10 items-start">

          {/* ── Left: Info ── */}
          <div className="flex-1 min-w-0">

            {/* Description */}
            <h2 className="text-xl font-bold text-gray-900 mb-3">About This Package</h2>
            <p className="text-sm text-gray-600 leading-relaxed mb-6">{pkg.description}</p>

            {/* Hotel info */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {[
                { city: "Makkah al-Mukarramah", hotel: pkg.hotelMakkah, distance: pkg.distanceMakkah, icon: "ri-hotel-line", color: "bg-amber-50 border-amber-200" },
                { city: "Madinah al-Munawwarah", hotel: pkg.hotelMadinah, distance: pkg.distanceMadinah, icon: "ri-hotel-line", color: "bg-green-50 border-green-200" },
              ].map(h => (
                <div key={h.city} className={`${h.color} border rounded-xl p-4`}>
                  <p className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-1">{h.city}</p>
                  <p className="text-sm font-bold text-gray-900">{h.hotel}</p>
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-4 h-4 flex items-center justify-center">
                      <i className="ri-walk-line text-xs text-gray-400"></i>
                    </div>
                    <span className="text-xs text-gray-500">{h.distance}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Banner image */}
            <div className="w-full h-56 rounded-xl overflow-hidden mb-8">
              <img src={pkg.bannerImage} alt={pkg.title} className="w-full h-full object-cover object-center" />
            </div>

            {/* Highlights */}
            <div className="bg-cyan-50 rounded-xl p-5 mb-8">
              <h3 className="text-sm font-bold text-cyan-700 mb-4 uppercase tracking-wide">Package Highlights</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {pkg.highlights.map(h => (
                  <div key={h} className="flex items-start gap-2">
                    <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-checkbox-circle-fill text-cyan-600 text-base"></i>
                    </div>
                    <span className="text-sm text-gray-700">{h}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Itinerary */}
            <h3 className="text-base font-bold text-gray-900 mb-4">Day-by-Day Itinerary</h3>
            <div className="space-y-2 mb-8">
              {pkg.itinerary.map(day => (
                <div key={day.day} className="border border-gray-200 rounded-lg overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setOpenDay(openDay === day.day ? null : day.day)}
                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 flex items-center justify-center rounded-full bg-cyan-600 text-white text-xs font-bold flex-shrink-0">
                        {day.day}
                      </span>
                      <span className="text-sm font-semibold text-gray-800">{day.title}</span>
                    </div>
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                      <i className={`ri-arrow-${openDay === day.day ? "up" : "down"}-s-line text-gray-500 text-lg`}></i>
                    </div>
                  </button>
                  {openDay === day.day && (
                    <div className="px-4 py-3 border-t border-gray-100">
                      <ul className="space-y-1.5">
                        {day.activities.map((act, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-gray-600">
                            <div className="w-4 h-4 flex items-center justify-center flex-shrink-0 mt-0.5">
                              <i className="ri-moon-line text-amber-500 text-xs"></i>
                            </div>
                            {act}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Inclusions & Exclusions */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div className="bg-green-50 rounded-xl p-5">
                <h4 className="text-sm font-bold text-green-700 mb-3 flex items-center gap-2">
                  <i className="ri-checkbox-circle-line text-green-600"></i> What&apos;s Included
                </h4>
                <ul className="space-y-1.5">
                  {pkg.inclusions.map(inc => (
                    <li key={inc} className="flex items-start gap-2 text-xs text-gray-700">
                      <i className="ri-check-line text-green-500 flex-shrink-0 mt-0.5"></i>
                      {inc}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="bg-red-50 rounded-xl p-5">
                <h4 className="text-sm font-bold text-red-700 mb-3 flex items-center gap-2">
                  <i className="ri-close-circle-line text-red-500"></i> Not Included
                </h4>
                <ul className="space-y-1.5">
                  {pkg.exclusions.map(exc => (
                    <li key={exc} className="flex items-start gap-2 text-xs text-gray-700">
                      <i className="ri-close-line text-red-400 flex-shrink-0 mt-0.5"></i>
                      {exc}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          {/* ── Right: Inquiry Form ── */}
          <div className="w-full lg:w-96 flex-shrink-0">
            <div className="sticky top-20">
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <div className="bg-gradient-to-r from-cyan-600 to-cyan-500 px-5 py-4">
                  <h3 className="text-white font-bold text-base">Book {pkg.label} Package</h3>
                  <p className="text-cyan-100 text-xs mt-0.5">Send your details and we&apos;ll respond within 24 hours with pricing</p>
                </div>

                {submitted ? (
                  <div className="p-8 text-center">
                    <div className="w-14 h-14 flex items-center justify-center rounded-full bg-green-100 mx-auto mb-4">
                      <i className="ri-check-line text-2xl text-green-600"></i>
                    </div>
                    <h4 className="text-base font-bold text-gray-900 mb-1">Inquiry Sent!</h4>
                    <p className="text-sm text-gray-500 mb-1">Jazak Allah Khair for reaching out.</p>
                    <p className="text-sm text-gray-500 mb-4">
                      Our team will contact you within 24 hours with the complete package pricing.
                    </p>
                    <button type="button"
                      onClick={() => { setSubmitted(false); setName(""); setPhone(""); setEmail(""); setCity(""); setDepartureDate(""); setPilgrims("2"); setMessage(""); }}
                      className="text-sm text-cyan-600 font-semibold cursor-pointer hover:underline whitespace-nowrap">
                      Submit Another
                    </button>
                  </div>
                ) : (
                  <form data-readdy-form onSubmit={handleSubmit} className="p-5 space-y-4">
                    {/* Package info */}
                    <div className="bg-gray-50 rounded-lg px-4 py-3">
                      <p className="text-xs text-gray-500 mb-0.5">Selected Package</p>
                      <p className="text-sm font-bold text-gray-900">{pkg.title}</p>
                      <p className="text-xs text-cyan-600 mt-0.5">{pkg.nights} Nights · Makkah + Madinah</p>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Full Name <span className="text-red-500">*</span></label>
                      <input name="name" value={name} onChange={e => setName(e.target.value)}
                        placeholder="Your full name"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Phone No. <span className="text-red-500">*</span></label>
                      <div className="flex items-center border border-gray-200 rounded-md overflow-hidden focus-within:border-cyan-400 transition-colors">
                        <span className="px-3 py-2.5 bg-gray-50 border-r border-gray-200 text-sm text-gray-600 whitespace-nowrap select-none">+92</span>
                        <input name="phone" value={phone} onChange={e => setPhone(e.target.value.replace(/\D/g, ""))}
                          placeholder="3001234567" maxLength={10}
                          className="flex-1 px-3 py-2.5 text-sm outline-none bg-transparent" />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Email <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <input name="email" type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="you@example.com"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">Your City <span className="text-red-500">*</span></label>
                      <input name="city" value={city} onChange={e => setCity(e.target.value)}
                        placeholder="e.g. Lahore"
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors" />
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Departure Date <span className="text-red-500">*</span></label>
                        <input name="departureDate" type="date" value={departureDate} onChange={e => setDepartureDate(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors cursor-pointer" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-gray-600 mb-1">Pilgrims</label>
                        <select name="pilgrims" value={pilgrims} onChange={e => setPilgrims(e.target.value)}
                          className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 cursor-pointer bg-white appearance-none">
                          {["1","2","3","4","5","6","7","8","9","10+"].map(n => (
                            <option key={n} value={n}>{n} {n === "1" ? "Pilgrim" : "Pilgrims"}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-1">
                        Message <span className="text-gray-400 font-normal">(Optional)</span>
                      </label>
                      <textarea name="message" value={message} onChange={e => setMessage(e.target.value)}
                        maxLength={500} rows={3}
                        placeholder="Any special requirements or questions..."
                        className="w-full border border-gray-200 rounded-md px-3 py-2.5 text-sm focus:outline-none focus:border-cyan-400 transition-colors resize-none" />
                      <p className="text-xs text-gray-400 text-right mt-0.5">{message.length}/500</p>
                    </div>

                    {error && (
                      <p className="text-sm text-red-500 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</p>
                    )}

                    <button type="submit" disabled={loading}
                      className="w-full flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 text-white text-sm font-bold py-3 rounded-md cursor-pointer transition-colors whitespace-nowrap">
                      <i className="ri-send-plane-fill text-base"></i>
                      {loading ? "Sending..." : "GET PRICE & BOOK"}
                    </button>

                    <p className="text-xs text-gray-400 text-center">
                      We respond within 24 hours · 03711432451
                    </p>
                  </form>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>

      <Footer />
    </div>
  );
}
